import { type DeviceItem, useLocaleStore, useFirmwareStore } from '@/stores'
import { computed, ref } from 'vue'
import {
  ConnectionType,
  VersionInfo,
  DeviceType,
  type KeyboardPerfCapability,
} from '@rapoo/device-sdk'
import { useConfigName } from '@/composables/useConfigName'

export function useBaseData(itemRef: { value: DeviceItem }) {
  const firmwareStore = useFirmwareStore()
  const device = computed(() => itemRef.value.original)
  const { t } = useLocaleStore()

  // 固件版本（从 store 缓存或主动读取，跟随当前 item 的 device）
  const firmwareVersions = ref<VersionInfo[] | null>(null)
  const initFirmwareVersion = async () => {
    const versionInfos = await firmwareStore.getDeviceVersions(device.value)
    firmwareVersions.value = versionInfos ?? null
  }

  const keyboardPerfCap = computed(() =>
    device.value?.manifest.capabilities.find(
      (c): c is KeyboardPerfCapability => c.type === 'keyboardPerf',
    ),
  )

  const reportRate = ref<number | null>(null)
  const sleepTime = ref<number | null>(null)

  const initKeyboardPerf = async () => {
    reportRate.value = null
    sleepTime.value = null

    if (device.value?.manifest.type !== 'keyboard') return

    const perf = device.value.features.keyboardPerfCfg
    const cap = keyboardPerfCap.value
    if (!perf || !cap) return

    const tasks: Promise<void>[] = []

    if (cap.config.reportRateOptions?.length) {
      tasks.push(
        perf
          .getReportRate()
          .then((rate) => {
            reportRate.value = rate
          })
          .catch((err) => {
            console.warn('[useBaseData] failed to read keyboard report rate:', err)
          }),
      )
    }

    if (cap.config.sleepTimeRange) {
      tasks.push(
        perf
          .getSleepTime()
          .then((time) => {
            sleepTime.value = time
          })
          .catch((err) => {
            console.warn('[useBaseData] failed to read keyboard sleep time:', err)
          }),
      )
    }

    await Promise.all(tasks)
  }

  const initBaseData = async () => {
    await Promise.all([initFirmwareVersion(), initKeyboardPerf()])
  }

  // 当前配置
  const configName = computed(() => {
    return useConfigName(device.value.currentConfig, device.value)
  })

  // 连接方式
  const connectType = computed(() => {
    return device.value.getConnectionType() === ConnectionType.Wired
      ? t('home.overview.baseData.wired')
      : t('home.overview.baseData.wireless')
  })

  const baseData = computed(() => {
    const data = [
      // 型号
      {
        name: t('home.overview.baseData.model'),
        value: itemRef.value.name,
      },
      // 当前配置
      {
        name: t('home.overview.baseData.currentConfig'),
        value: configName.value,
      },
      // 连接方式
      {
        name: t('home.overview.baseData.connectionType'),
        value: connectType.value,
      },
    ]
    if (firmwareVersions.value) {
      // 固件版本
      data.push({
        name: t('home.overview.baseData.firmwareVersion'),
        value: firmwareVersions.value
          .filter((v) => v.deviceType !== DeviceType.Receiver)
          .map((v) => v.version)
          .join('-'),
      })
      const connectionType = device.value.getConnectionType()
      if (connectionType === ConnectionType.Wireless) {
        data.push({
          name: t('home.overview.baseData.receiverVersion'),
          value: firmwareVersions.value
            .filter((v) => v.deviceType === DeviceType.Receiver)
            .map((v) => v.version)
            .join('-'),
        })
      }
    }

    // 键盘 USB 回报率
    if (reportRate.value !== null) {
      const label = keyboardPerfCap.value?.config.reportRateOptions?.find(
        (option) => option.value === reportRate.value,
      )?.label
      if (label) {
        data.push({
          name: t('home.overview.baseData.reportRate'),
          value: label,
        })
      }
    }

    // 键盘无线休眠时间
    if (sleepTime.value !== null) {
      data.push({
        name: t('home.overview.baseData.sleepTime'),
        value: `${sleepTime.value}${t('home.overview.baseData.minutes')}`,
      })
    }

    if (!data.length) return null

    return {
      title: t('home.overview.baseData.title'),
      data,
    }
  })

  return {
    baseData,
    initBaseData,
  }
}
